import * as React from 'react';
import { connect } from 'react-redux';
import { isNil, omit } from 'lodash';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

import ActionButton from 'components/buttons/action-button/ActionButton';
import InstagramDataEnabledBadge from 'components/instagram-data-enabled/badge/InstagramDataEnabledBadge';

import DeliverablesMetricsAttachmentForm from 'modules/admin/components/deliverables/attachment-form/DeliverablesMetricsAttachmentForm';
import DeliverablesMetricsInstagramPostForm from 'modules/admin/components/deliverables/DeliverablesMetricsInstagramPostForm';
import DeliverablesMetricsInstagramStoryForm from 'modules/admin/components/deliverables/metrics-story-form/DeliverablesMetricsInstagramStoryForm';
import DeliverablesMetricsReachedCountriesForm from 'modules/admin/components/deliverables/deliverables-metrics-reached-countries-form/DeliverablesMetricsReachedCountriesForm';
import DeliverablesMetricsTikTokVideoForm from 'modules/admin/components/deliverables/metrics-tiktok-video-form/DeliverablesMetricsTikTokVideoForm';
import DroppableAttachmentContainer from 'modules/admin/components/deliverables/dnd/DroppableAttachmentContainer';

import DeliverablesMetricsFormHeader from '../metrics-form/DeliverablesMetricsFormHeader';
import DeliverablesMetricsInstagramReelForm from '../metrics-instagram-reel-form/DeliverablesMetricsInstagramReelForm';

import {
  addReportMetrics,
  changeAdditionalDeliverable,
  deleteReportStoryMetrics,
  fetchReportMetricAnalytic,
  reportMetricsReorder,
  reportMetricsSnippetMatch,
  updateReportMetrics
} from 'modules/campaigns/components/campaign-report/metrics/reducers/campaign-report-metrics';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  getInstagramAnalytics,
  getTikTokAnalytics
} from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

import './deliverables-metrics-info.scss';

applyTranslations(require('./deliverables-metrics-info.i18n.json'));

import type {
  AdminAnalytic,
  AdminDeliverable,
  Deliverable,
  DeliverableAnalytic,
  DeliverableTag,
  DeliverableTikTokAnalytic,
  DeliverableType,
  DeliverableUploadedEventLabel
} from 'types';

type OwnProps = {
  deliverablesMetricsItem: Deliverable,
  deliverableUploadedEventLabel: DeliverableUploadedEventLabel,
  deliverableTypes: Array<DeliverableType>,
  isFullscreen?: boolean,
  useFacebookDataLayout?: boolean,
  hasActiveFBConnection: boolean,
  onDelete: (b: number, a: number) => void,
  onUpdate: () => void
};

type StateProps = {
  deliverablesMetricsItem: AdminDeliverable
};

type DispatchProps = {
  dispatchAddDeliverableMetrics: (deliverableId: number, campaignId: number, metricId?: number | null, data?: Partial<AdminAnalytic> | null) => Promise<void>,
  dispatchFetchDeliverableAnalytic: (id: number) => void,
  dispatchMetricsSnippetMatch: (deliverableId: number, attachmentId: number, rowId: number, sourceRowId: number) => void,
  dispatchMetricsAnalyticReorder: (deliverableId: number, from: number, to: number) => void,
  dispatchUpdateDeliverableMetrics: (deliverableId: number, campaignId: number, metricsId: number, data: Partial<AdminAnalytic>) => Promise<void>,
  dispatchDeleteDeliverableMetricsAnalytics: (deliverableId: number, attachmentId: number, metricsId: number) => void,
  dispatchUnMatchDeliverableAttachment: (deliverableId: number, attachmentId: number, sourceAnalyticId: number) => void,
  dispatchChangeAdditionalDeliverable: (
    deliverableId: number,
    offerId: number,
    data: any
  ) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      title: string,
      onConfirm: () => void,
      onCancel: () => void,
      confirmButtonLabel: string
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class DeliverablesMetricsInfo extends React.PureComponent<Props> {
  static defaultProps = {
    isFullscreen: true
  };

  private handleMetricsUpdated = (): void => {
    const { deliverablesMetricsItem, dispatchFetchDeliverableAnalytic } = this.props;

    dispatchFetchDeliverableAnalytic(deliverablesMetricsItem.id);
  };

  private handleDragEnd = (params: typeof DropResult) => {
    if (!params.source || !params.destination) {
      return;
    }

    const {
      deliverablesMetricsItem,
      dispatchDeleteDeliverableMetricsAnalytics,
      dispatchMetricsSnippetMatch,
      dispatchMetricsAnalyticReorder
    } = this.props;

    const sourceId = params.source.droppableId;
    const metricTableRowIdPart = 'metrics-table-row-';
    const isMatching = sourceId.includes(metricTableRowIdPart);
    const isReorder = sourceId.includes('metrics-sortable-table-row');

    if (isMatching) {
      const attachmentId = Number(params.draggableId);
      const attachment = (deliverablesMetricsItem.postAttachments ?? [])
        .find(att => att.id === attachmentId);
      // matching snippets/analytics with row
      const [deliverableId, rowId] = params.destination.droppableId
        .replace(metricTableRowIdPart, '')
        .split('|')
        .map(id => Number(id));

      const [, sourceRowId] = sourceId
        .replace(metricTableRowIdPart, '')
        .split('|')
        .map(id => Number(id));

      if (sourceRowId === -1 && rowId === -1) {
        // match from unsorted to unsorted - simply skip that
        return;
      }

      if (attachment?.meta.type === 'highres' && rowId === -1) {
        dispatchDeleteDeliverableMetricsAnalytics(
          deliverableId,
          deliverablesMetricsItem.campaign.id,
          sourceRowId
        );
      } else {
        // row index can be set to non-existing id to reset match and move attachment to unmatched stories
        dispatchMetricsSnippetMatch(deliverableId, attachmentId, rowId, sourceRowId);
      }
    } else if (isReorder) {
      // reorder rows
      const deliverableId = Number(params.destination.droppableId.replace('metrics-sortable-table-row-', ''));

      dispatchMetricsAnalyticReorder(deliverableId, params.source.index, params.destination.index);
    }
  };

  private handleAddAnalytic = () => {
    const { deliverablesMetricsItem, dispatchAddDeliverableMetrics } = this.props;

    return dispatchAddDeliverableMetrics(deliverablesMetricsItem.id, deliverablesMetricsItem.campaign.id);
  };

  private handleDelete = () => {
    const { deliverablesMetricsItem, onDelete } = this.props;

    if (deliverablesMetricsItem.offerId) {
      onDelete(deliverablesMetricsItem.offerId, deliverablesMetricsItem.id);
    }
  };

  private handleTikTokOrIGReelAnalyticUpdate = (analytic?: DeliverableAnalytic | DeliverableTikTokAnalytic | null) => (deliverableId: number, campaignId: number, metric: Partial<Partial<AdminAnalytic>>) => {
    const {
      dispatchAddDeliverableMetrics,
      dispatchUpdateDeliverableMetrics
    } = this.props;

    if (analytic) {
      // $FlowIgnore - Cannot determine a type for merged object
      const mergedAnalytic: Partial<AdminAnalytic> = {
        ...analytic,
        ...metric
      };

      return dispatchUpdateDeliverableMetrics(deliverableId, campaignId, analytic.id, mergedAnalytic);
    }

    return dispatchAddDeliverableMetrics(deliverableId, campaignId, null, metric);
  };

  private handleUpdateAdditionlDeliverableTag = (tag?: DeliverableTag | null) => {
    const { deliverablesMetricsItem, dispatchChangeAdditionalDeliverable } = this.props;

    dispatchChangeAdditionalDeliverable(deliverablesMetricsItem.id, deliverablesMetricsItem.offer.id, {
      tag,
      deliverableType: deliverablesMetricsItem.deliverableType
    });
  };

  private isDeliverableEmpty(): boolean {
    const { deliverablesMetricsItem } = this.props;
    const hasNoAttachments = isNil(deliverablesMetricsItem.postAttachments) || deliverablesMetricsItem.postAttachments.length === 0;
    const hasNoPost = isNil(deliverablesMetricsItem.post);
    const hasNoAnalytics = deliverablesMetricsItem.analytics
      .filter(metric => Object.values(omit(metric, 'metricsType')).some(Boolean)).length === 0;

    return hasNoAttachments && hasNoPost && hasNoAnalytics;
  }

  private changeDeliverableType(
    deliverableType: DeliverableType,
    tag: DeliverableTag | undefined | null
  ) {
    const {
      deliverablesMetricsItem,
      dispatchChangeAdditionalDeliverable
    } = this.props;

    return dispatchChangeAdditionalDeliverable(
      deliverablesMetricsItem.id,
      deliverablesMetricsItem.offer.id,
      {
        tag,
        deliverableType
      }
    );
  }

  private handleUpdateAdditionlDeliverableType = (
    deliverableType: DeliverableType,
    hasTaskTypeChanged: boolean,
    tag: DeliverableTag | undefined | null
  ) => {
    const {
      onUpdate,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal
    } = this.props;

    if (this.isDeliverableEmpty()) {
      // it's safe to change since nothing would be lost
      this.changeDeliverableType(deliverableType, tag);
      return;
    }

    const msgKey = 'deliverable_metrics_info.deliverable_change_type_confirmation';
    const additionalMessage = hasTaskTypeChanged ? ` ${__t(`${msgKey}.message_additional`)}` : '';
    const modalMessage = `${__t(`${msgKey}.message`)}${additionalMessage}${__t(`${msgKey}.message_confirm`)}`;

    dispatchShowConfirmationModal({
      title: __t('deliverable_metrics_info.deliverable_change_type_confirmation.title'),
      message: modalMessage,
      confirmButtonLabel: __t('deliverable_metrics_info.deliverable_change_type_confirmation.button_confirm'),
      onConfirm: () => this.changeDeliverableType(deliverableType, tag).then(() => {
        onUpdate();
        dispatchHideConfirmationModal();
      }),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  private renderInstagramPostMetrics(): React.ReactNode {
    const { deliverablesMetricsItem, deliverableUploadedEventLabel, useFacebookDataLayout } = this.props;
    const analytic = getInstagramAnalytics(deliverablesMetricsItem.analytics || [])[0];

    return (
      <div className="deliverables-metrics-info__item">
        {
          !useFacebookDataLayout && (
            <DeliverablesMetricsInstagramPostForm
              deliverableId={deliverablesMetricsItem.id}
              analytic={analytic}
            />
          )
        }
        <DeliverablesMetricsAttachmentForm
          deliverable={deliverablesMetricsItem}
          offerId={deliverablesMetricsItem.offer.id}
          onUpdate={this.handleMetricsUpdated}
          deliverableUploadedEventLabel={deliverableUploadedEventLabel}
          hideAnalyticsColumn={useFacebookDataLayout}
        />
      </div>
    );
  }

  private renderInstagramReelMetrics(): React.ReactNode {
    const { deliverablesMetricsItem, deliverableUploadedEventLabel, useFacebookDataLayout } = this.props;
    const analytic = getInstagramAnalytics(deliverablesMetricsItem.analytics || [])[0];

    return (
      <div className="deliverables-metrics-info__item">
        {
          !useFacebookDataLayout && (
            <DeliverablesMetricsInstagramReelForm
              deliverableId={deliverablesMetricsItem.id}
              campaignId={deliverablesMetricsItem.campaign.id}
              analytic={analytic}
              onUpdate={this.handleTikTokOrIGReelAnalyticUpdate(analytic)}
            />
          )
        }
        <DeliverablesMetricsAttachmentForm
          deliverable={deliverablesMetricsItem}
          offerId={deliverablesMetricsItem.offer.id}
          onUpdate={this.handleMetricsUpdated}
          deliverableUploadedEventLabel={deliverableUploadedEventLabel}
          hideAnalyticsColumn={useFacebookDataLayout}
        />
      </div>
    );
  }

  private renderInstagramStoryMetrics(): React.ReactNode {
    const {
      deliverablesMetricsItem,
      isFullscreen,
      deliverableUploadedEventLabel,
      useFacebookDataLayout,
      dispatchDeleteDeliverableMetricsAnalytics,
      dispatchUnMatchDeliverableAttachment,
      dispatchUpdateDeliverableMetrics
    } = this.props;

    const analytics = getInstagramAnalytics(deliverablesMetricsItem.analytics || []);

    if (useFacebookDataLayout) {
      return null;
    }

    return (
      <div className="deliverables-metrics-info__item">
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <DeliverablesMetricsInstagramStoryForm
            deliverableId={deliverablesMetricsItem.id}
            analytics={analytics}
            attachments={deliverablesMetricsItem.postAttachments}
            onUpdateDeliverableMetrics={dispatchUpdateDeliverableMetrics}
            onDeleteDeliverableMetricsAnalytics={dispatchDeleteDeliverableMetricsAnalytics}
            onAddDeliverableMetric={this.handleAddAnalytic}
            onUnMatchDeliverableAttachment={dispatchUnMatchDeliverableAttachment}
            isFullscreen={isFullscreen}
          />
          <DroppableAttachmentContainer
            id={deliverablesMetricsItem.id}
            analyticId={-1}
          >
            <DeliverablesMetricsAttachmentForm
              header={__t('deliverable_metrics_info.unmatched_stories_label')}
              deliverable={deliverablesMetricsItem}
              offerId={deliverablesMetricsItem.offer.id}
              onUpdate={this.handleMetricsUpdated}
              deliverableUploadedEventLabel={deliverableUploadedEventLabel}
            />
          </DroppableAttachmentContainer>
        </DragDropContext>
      </div>
    );
  }

  private renderTikTokVideoMetrics(): React.ReactNode {
    const { deliverablesMetricsItem, deliverableUploadedEventLabel } = this.props;
    const analytic = getTikTokAnalytics(deliverablesMetricsItem.analytics || [])[0];

    return (
      <div className="deliverables-metrics-info__item">
        <DeliverablesMetricsTikTokVideoForm
          deliverableId={deliverablesMetricsItem.id}
          campaignId={deliverablesMetricsItem.campaign.id}
          analytic={analytic}
          onUpdate={this.handleTikTokOrIGReelAnalyticUpdate(analytic)}
        />
        <DeliverablesMetricsReachedCountriesForm
          deliverableId={deliverablesMetricsItem.id}
          analytic={analytic}
          campaignId={deliverablesMetricsItem.campaign.id}
          onUpdate={this.handleTikTokOrIGReelAnalyticUpdate(analytic)}
        />
        <DeliverablesMetricsAttachmentForm
          deliverable={deliverablesMetricsItem}
          offerId={deliverablesMetricsItem.offer.id}
          onUpdate={this.handleMetricsUpdated}
          deliverableUploadedEventLabel={deliverableUploadedEventLabel}
        />
      </div>
    );
  }

  private renderContent() {
    const { deliverablesMetricsItem } = this.props;

    switch (deliverablesMetricsItem.deliverableType) {
      case DELIVERABLE_TYPE_INSTAGRAM_POST:
        return this.renderInstagramPostMetrics();
      case DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST:
        return this.renderInstagramPostMetrics();
      case DELIVERABLE_TYPE_INSTAGRAM_STORY:
        return this.renderInstagramStoryMetrics();
      case DELIVERABLE_TYPE_INSTAGRAM_REGRAM_STORY:
        return this.renderInstagramStoryMetrics();
      case DELIVERABLE_TYPE_INSTAGRAM_REEL:
        return this.renderInstagramReelMetrics();
      case DELIVERABLE_TYPE_TIKTOK_VIDEO:
        return this.renderTikTokVideoMetrics();
      default:
        return null;
    }
  }

  render(): React.ReactNode {
    const { deliverablesMetricsItem, deliverableTypes, hasActiveFBConnection } = this.props;
    const isInstagramTask = deliverablesMetricsItem.deliverableType.includes('instagram');
    const isStory = deliverablesMetricsItem.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY;

    return (
      <div className="deliverables-metrics-info">
        <div className="deliverables-metrics-info__header">
          <div className="deliverables-metrics-info__header-text">
            <div className="deliverables-metrics-info__heading">
              {__t('deliverable_metrics_info.heading')}
            </div>
            {
              isInstagramTask && (
                <div className="deliverables-metrics-info__subheading">
                  {__t('deliverable_metrics_info.subheading1')}
                  {
                  isStory
                    ? ` ${__t('deliverable_metrics_info.subheading2')}`
                    : null
                  }
                  &nbsp;{__t('deliverable_metrics_info.subheading3')}
                </div>
              )
            }
          </div>
          <div className="deliverables-metrics-info__additional-interaction">
            {
              isInstagramTask && (
                <div className="deliverables-metrics-info__data-enabled">
                  <InstagramDataEnabledBadge
                    hasActiveFbConnection={hasActiveFBConnection}
                    influencerCheckOverride
                  />
                </div>
              )
            }
            {
              deliverablesMetricsItem.additional ?
                (
                  <ActionButton
                    color="secondary"
                    className="deliverables-metrics-info__button"
                    onClick={this.handleDelete}
                  >
                    {__t('deliverable_metrics_info.button_delete')}
                  </ActionButton>
                ) : null
            }
          </div>
        </div>
        {
          deliverablesMetricsItem.additional && (
            <DeliverablesMetricsFormHeader
              deliverable={deliverablesMetricsItem}
              deliverableTypes={deliverableTypes}
              onTagUpdate={this.handleUpdateAdditionlDeliverableTag}
              onTypeUpdate={this.handleUpdateAdditionlDeliverableType}
            />
          )
        }
        {this.renderContent()}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddDeliverableMetrics: (deliverableId, campaignId, metricsId, data = {}) =>
    dispatch(addReportMetrics(deliverableId, campaignId, {
      ...data,
      deliverableId,
      metricsType: data?.metricsType || 'instagram_story_metrics'
    })),

  dispatchFetchDeliverableAnalytic: (id) => {
    dispatch(fetchReportMetricAnalytic(id));
  },

  dispatchMetricsSnippetMatch: (deliverableId, attachmentId, rowId, sourceRowId) => {
    dispatch(reportMetricsSnippetMatch(deliverableId, attachmentId, rowId, sourceRowId));
  },

  dispatchMetricsAnalyticReorder: (deliverableId, from, to) => {
    dispatch(reportMetricsReorder(deliverableId, from, to));
  },

  dispatchUpdateDeliverableMetrics: (deliverableId, campaignId, metricsId, data) =>
    dispatch(updateReportMetrics(deliverableId, campaignId, metricsId, data)),

  dispatchDeleteDeliverableMetricsAnalytics: (deliverableId, attachmentId, metricsId) =>
    dispatch(deleteReportStoryMetrics(deliverableId, attachmentId, metricsId)),

  dispatchUnMatchDeliverableAttachment: (deliverableId, attachmentId, sourceAnalyticId) =>
    dispatch(reportMetricsSnippetMatch(deliverableId, attachmentId, -1, sourceAnalyticId)),

  dispatchChangeAdditionalDeliverable: (deliverableId, offerId, data) => (
    dispatch(changeAdditionalDeliverable(deliverableId, offerId, data))
  ),

  dispatchShowConfirmationModal: (params: {
    message: string,
    title: string,
    onConfirm: () => void,
    onCancel: () => void,
    confirmButtonLabel: string
  }) => dispatch(showConfirmModal(params)),

  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

const mapStateToProps = ({ campaignReportingMetrics }, props: OwnProps): StateProps => {
  const deliverablesList = campaignReportingMetrics.deliverables.items || [];

  return {
    deliverablesMetricsItem: deliverablesList
      .find(d => d.id === props.deliverablesMetricsItem.id) ?? props.deliverablesMetricsItem
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DeliverablesMetricsInfo);
